import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ModelPerformanceMapData } from 'types/Meteorology/Fields/ModelPerformance/mapData';

export interface ModelPerformanceMapError {
  title: string;
  message: string;
}

interface MeteorologyFieldsModelPerformanceState {
  periodType: string | undefined;
  analysisType: string | undefined;
  variable: string | undefined;
  deadline: string | undefined;
  rounds: string[] | undefined;
  /** Intervalo escolhido no calendário de Rodadas (yyyy-MM-dd); as rodadas saem dele. */
  roundsRange: { start: string; end: string } | undefined;
  pentad: string | undefined;
  analysisPeriod: { start: string; end: string } | undefined;
  selectedRounds: string[] | undefined;
  mapData: ModelPerformanceMapData | undefined;
  mapLoading: boolean;
  mapError: ModelPerformanceMapError | undefined;
  /** Troca de pêntada com consulta já feita: o mapa da nova pêntada sai sozinho. */
  replotPending: boolean;
}

const initialState: MeteorologyFieldsModelPerformanceState = {
  periodType: undefined,
  analysisType: undefined,
  variable: undefined,
  deadline: undefined,
  rounds: undefined,
  roundsRange: undefined,
  pentad: undefined,
  analysisPeriod: undefined,
  selectedRounds: undefined,
  mapData: undefined,
  mapLoading: false,
  mapError: undefined,
  replotPending: false,
};

export const meteorologyFieldsModelPerformanceSlice = createSlice({
  name: 'meteorologyFieldsModelPerformance',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<MeteorologyFieldsModelPerformanceState>>) => {
      Object.assign(state, action.payload);
    },
    // Troca entre Pêntada 1/2/3 (sugestão do Palin, 2026-10-28; mesmo fluxo do
    // setGranularidadeSH do Windx): mantém filtros, rodadas e intervalo. As
    // rodadas são recalculadas contra a lista da nova pêntada pelo
    // usePentadRoundsSync. Se já havia consulta, o mapa da pêntada anterior sai
    // da tela e a da nova é consultada sozinha.
    switchPentad: (state, action: PayloadAction<string>) => {
      if (state.pentad === action.payload) return;
      const consultou = Boolean(state.mapData || state.mapError || state.mapLoading);
      state.pentad = action.payload;
      state.mapData = undefined;
      state.mapError = undefined;
      state.replotPending = consultou;
      state.mapLoading = consultou;
    },
    clearFilters: () => ({ ...initialState }),
  },
});

export const selectMeteorologyFieldsModelPerformancePeriodType = (state: any) => (
  state.meteorologyFieldsModelPerformance.periodType
);
export const selectMeteorologyFieldsModelPerformanceAnalysisType = (state: any) => (
  state.meteorologyFieldsModelPerformance.analysisType
);
export const selectMeteorologyFieldsModelPerformanceVariable = (state: any) => (
  state.meteorologyFieldsModelPerformance.variable
);
export const selectMeteorologyFieldsModelPerformanceRounds = (state: any) => (
  state.meteorologyFieldsModelPerformance.rounds
);
export const selectMeteorologyFieldsModelPerformancePentad = (state: any) => (
  state.meteorologyFieldsModelPerformance.pentad
);
export const selectMeteorologyFieldsModelPerformanceRoundsRange = (state: any) => (
  state.meteorologyFieldsModelPerformance.roundsRange
);
export const selectMeteorologyFieldsModelPerformanceReplotPending = (state: any): boolean => (
  state.meteorologyFieldsModelPerformance.replotPending
);
export const selectMeteorologyFieldsModelPerformanceAnalysisPeriod = (state: any) => (
  state.meteorologyFieldsModelPerformance.analysisPeriod
);
export const selectMeteorologyFieldsModelPerformanceDeadline = (state: any) => (
  state.meteorologyFieldsModelPerformance.deadline
);
export const selectMeteorologyFieldsModelPerformanceSelectedRounds = (state: any) => (
  state.meteorologyFieldsModelPerformance.selectedRounds
);
export const selectMeteorologyFieldsModelPerformanceMapData = (state: any) => (
  state.meteorologyFieldsModelPerformance.mapData
);
export const selectMeteorologyFieldsModelPerformanceMapLoading = (state: any) => (
  state.meteorologyFieldsModelPerformance.mapLoading
);
export const selectMeteorologyFieldsModelPerformanceMapError = (state: any) => (
  state.meteorologyFieldsModelPerformance.mapError
);

export const {
  setFilter, switchPentad, clearFilters,
} = meteorologyFieldsModelPerformanceSlice.actions;
export default meteorologyFieldsModelPerformanceSlice.reducer;
